// One idempotency key per SUBMISSION, not per click.
//
// §06: `Idempotency-Key` is honoured on POST for 24h so "a retrying agent must
// not create a second deal". A fresh key on every button press would satisfy
// the header and defeat the guarantee — the retry after a timeout would create
// the second deal the header exists to prevent. So the key is minted once for a
// given request body and REUSED for as long as that body is unchanged.
//
// Keying on the body rather than on time or on a form-open event is what makes
// both halves true at once:
//
//   * Retrying the same submission (the network dropped, a 500 came back, the
//     user pressed Save again) sends the same body, gets the same key, and the
//     server returns the original result instead of creating a duplicate.
//   * Fixing a field after a `validation_failed` and saving again is a DIFFERENT
//     submission. Reusing the key there would be a client bug the API is
//     entitled to reject outright — `idempotency_key_reuse`, "same key,
//     different body" (§06) — so it gets a new one.

import { useCallback, useRef } from "react";

export interface IdempotencyKeys {
  /** The key for this exact body: stable across retries, new when the body changes. */
  keyFor: (body: unknown) => string;
}

function newKey(): string {
  return crypto.randomUUID();
}

export function useIdempotencyKey(): IdempotencyKeys {
  const held = useRef<{ signature: string; key: string } | null>(null);

  const keyFor = useCallback((body: unknown): string => {
    // The body is always built by the same code path from the same draft, so
    // key order is stable and this comparison is meaningful.
    const signature = JSON.stringify(body);
    const current = held.current;
    if (current !== null && current.signature === signature) return current.key;
    const key = newKey();
    held.current = { signature, key };
    return key;
  }, []);

  return { keyFor };
}
